import React, { useMemo, useState } from "react";
import _ from "lodash";
import { TableHeader } from "./survey-summary";
import { useTranslation } from "react-i18next";
import { useStore } from "../../../helpers";
import { ChevronDown, ChevronUp } from "lucide-react";
import { DownloadAsExcelSheet } from "../../../components";

interface UserSummaryTableProps {
  data: any[];
}

export const UserSummaryTable: React.FC<UserSummaryTableProps> = ({ data }) => {
  const [t] = useTranslation(["survey_management", "analytical_summary"]);
  const { surveyResponseStore, userStore } = useStore();
  const [expandedUser, setExpandedUser] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  // 🧮 Group and summarize user data
  const userSummaryData = useMemo(() => {
    if (!data || data.length === 0) return [];

    const grouped = _.groupBy(data, "surveyor_name");
    return Object.keys(grouped).map((name) => {
      const userData = grouped[name];
      const booths = _.uniq(userData.map((r) => r.piv_level2));

      const total = _.sumBy(userData, "total");
      const valid = _.sumBy(userData, "valid");
      const invalid = _.sumBy(userData, "invalid");
      const notChecked = _.sumBy(userData, "not-checked");

      const boothDetails = _.chain(userData)
        .groupBy("piv_level2")
        .map((records, booth) => ({
          booth,
          total: _.sumBy(records, "total"),
          valid: _.sumBy(records, "valid"),
          invalid: _.sumBy(records, "invalid"),
          notChecked: _.sumBy(records, "not-checked"),
        }))
        .value();

      return {
        surveyor_name: name,
        booths,
        total,
        valid,
        invalid,
        notChecked,
        boothDetails,
      };
    });
  }, [data]);

  // 📊 Pagination
  const totalPages = Math.ceil(userSummaryData.length / rowsPerPage);
  const paginatedData = userSummaryData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );

  const nextPage = () =>
    currentPage < totalPages && setCurrentPage(currentPage + 1);
  const prevPage = () => currentPage > 1 && setCurrentPage(currentPage - 1);

  // 📄 Excel data
  const excelData = useMemo(() => {
    const rows: any[] = [];
    userSummaryData.forEach((user) => {
      rows.push({
        User: user.surveyor_name,
        "No. of Booths": user.booths.length,
        Valid: user.valid,
        Invalid: user.invalid,
        "Not Checked": user.notChecked,
        "Total Samples": user.total,
      });
      user.boothDetails.forEach((booth) => {
        rows.push({
          User: "",
          Booth: booth.booth,
          Valid: booth.valid,
          Invalid: booth.invalid,
          "Not Checked": booth.notChecked,
          "Total Samples": booth.total,
        });
      });
      rows.push({});
    });
    return rows;
  }, [userSummaryData]);

  const excelColumns = [
    { label: "User", key: "User" },
    { label: "Booth", key: "Booth" },
    { label: "No. of Booths", key: "No. of Booths" },
    { label: "Valid", key: "Valid" },
    { label: "Invalid", key: "Invalid" },
    { label: "Not Checked", key: "Not Checked" },
    { label: "Total Samples", key: "Total Samples" },
  ];

  const toggleExpand = (name: string) => {
    setExpandedUser((prev) => (prev === name ? null : name));
  };

  if (!data || _.isEmpty(data)) return null;

  return (
    <div className="mt-6">
      <div className="flex flex-row items-center justify-between">
        <TableHeader title={t("User Summary")} />
        {(userStore.isRoot() || userStore.isClient()) && (
          <DownloadAsExcelSheet
            data={excelData}
            cols={excelColumns}
            name="User-Summary"
          />
        )}
      </div>

      <div className="border border-gray-300 bg-white overflow-hidden rounded-xl">
        <table className="min-w-full text-sm text-gray-800 border-collapse">
          <thead className="bg-gray-200 border-b border-gray-400">
            <tr>
              <th className="px-3 py-2 text-left font-semibold border-r border-gray-300">
                {t("User")}
              </th>
              <th className="px-3 py-2 text-center font-semibold border-r border-gray-300">
                {t("Number of booths")}
              </th>
              <th className="px-3 py-2 text-center font-semibold border-r border-gray-300">
                {t("Valid")}
              </th>
              <th className="px-3 py-2 text-center font-semibold border-r border-gray-300">
                {t("Invalid")}
              </th>
              <th className="px-3 py-2 text-center font-semibold border-r border-gray-300">
                {t("Not Checked")}
              </th>
              <th className="px-3 py-2 text-center font-semibold">
                {t("Total Samples")}
              </th>
            </tr>
          </thead>

          <tbody>
            {paginatedData.map((user, index) => (
              <React.Fragment key={index}>
                <tr
                  onClick={() => toggleExpand(user.surveyor_name)}
                  className="hover:bg-gray-50 cursor-pointer border-b border-gray-200"
                >
                  <td className="px-3 py-2 border-r border-gray-300 font-medium flex items-center gap-2">
                    {/* 👇 Caret before name */}
                    {expandedUser === user.surveyor_name ? (
                      <ChevronUp size={14} className="text-gray-500" />
                    ) : (
                      <ChevronDown size={14} className="text-gray-500" />
                    )}
                    {user.surveyor_name}
                  </td>
                  <td className="px-3 py-2 border-r border-gray-300 text-center">
                    {user.booths.length}
                  </td>
                  <td className="px-3 py-2 border-r border-gray-300 text-center">
                    {user.valid}
                  </td>
                  <td className="px-3 py-2 border-r border-gray-300 text-center">
                    {user.invalid}
                  </td>
                  <td className="px-3 py-2 border-r border-gray-300 text-center">
                    {user.notChecked}
                  </td>
                  <td className="px-3 py-2 text-center">{user.total}</td>
                </tr>

                {expandedUser === user.surveyor_name && (
                  <tr>
                    <td colSpan={6} className="p-0">
                      <table className="min-w-full text-sm text-gray-700 border-t border-gray-300">
                        <thead className="bg-gray-100 border-b border-gray-300">
                          <tr>
                            <th className="px-3 py-1 text-left border-r border-gray-200">
                              {t("Booths")}
                            </th>
                            <th className="px-3 py-1 text-center border-r border-gray-200">
                              {t("Valid")}
                            </th>
                            <th className="px-3 py-1 text-center border-r border-gray-200">
                              {t("Invalid")}
                            </th>
                            <th className="px-3 py-1 text-center border-r border-gray-200">
                              {t("Not Checked")}
                            </th>
                            <th className="px-3 py-1 text-center">
                              {t("Total Samples")}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {user.boothDetails.map((booth, i) => (
                            <tr
                              key={i}
                              className="hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                              onClick={() =>
                                surveyResponseStore.update_new_filters(
                                  "piv_level2",
                                  booth.booth,
                                  `Booth : ${booth.booth}`,
                                )
                              }
                            >
                              <td className="px-3 py-1 border-r border-gray-200">
                                {booth.booth}
                              </td>
                              <td className="px-3 py-1 border-r border-gray-200 text-center">
                                {booth.valid}
                              </td>
                              <td className="px-3 py-1 border-r border-gray-200 text-center">
                                {booth.invalid}
                              </td>
                              <td className="px-3 py-1 border-r border-gray-200 text-center">
                                {booth.notChecked}
                              </td>
                              <td className="px-3 py-1 text-center">
                                {booth.total}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}

            {/* TOTAL Row */}
            <tr className="font-semibold border-t border-gray-400 bg-gray-50">
              <td className="px-3 py-2 text-left border-r border-gray-300">
                TOTAL
              </td>
              <td className="px-3 py-2 text-center border-r border-gray-300">
                {_.sumBy(userSummaryData, (u) => u.booths.length)}
              </td>
              <td className="px-3 py-2 text-center border-r border-gray-300">
                {_.sumBy(userSummaryData, "valid")}
              </td>
              <td className="px-3 py-2 text-center border-r border-gray-300">
                {_.sumBy(userSummaryData, "invalid")}
              </td>
              <td className="px-3 py-2 text-center border-r border-gray-300">
                {_.sumBy(userSummaryData, "notChecked")}
              </td>
              <td className="px-3 py-2 text-center">
                {_.sumBy(userSummaryData, "total")}
              </td>
            </tr>
          </tbody>
        </table>

        {/* Pagination */}
        <div className="flex justify-between items-center text-sm text-gray-800 mt-2 px-2 py-2">
          <span>
            Page <b>{currentPage}</b> of <b>{totalPages}</b>
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={prevPage}
              disabled={currentPage === 1}
              className={`px-2 py-1 rounded border ${
                currentPage === 1
                  ? "text-gray-400 border-gray-200"
                  : "hover:bg-gray-100"
              }`}
            >
              &lt;
            </button>
            <button
              onClick={nextPage}
              disabled={currentPage === totalPages}
              className={`px-2 py-1 rounded border ${
                currentPage === totalPages
                  ? "text-gray-400 border-gray-200"
                  : "hover:bg-gray-100"
              }`}
            >
              &gt;
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
